"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { SportsbookLogoMark } from "@/components/sportsbook-logos";
import { sportsbookIdToDisplay } from "@/lib/sportsbook-registry";
import { chipClass } from "@/components/fantasy-props/chrome";
import { hasNamedGroups, pickerGroups, type BookGroup, type BookRegulation } from "@/lib/book-catalog";
import { useBookCatalog } from "@/contexts/book-catalog-context";
import { useBookAvailability } from "@/contexts/book-availability-context";
import { NOT_BETTABLE_CLASS } from "@/lib/book-location";
import { US_STATES, normalizeUsState, type UsState } from "@/lib/us-states";
import { ScrollFade } from "./top-menus";

type Hit = { readonly id: string; readonly group: string | null };
export type CheckState = "on" | "off" | "some";

export function Check({ state }: { state: CheckState }) {
  return (
    <span aria-hidden="true" className={cn("flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border", state === "off" ? "border-zinc-600 text-transparent" : "border-primary bg-primary text-surface-base")}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">{state === "some" ? <path d="M6 12h12" /> : <path d="M20 6 9 17l-5-5" />}</svg>
    </span>
  );
}

export function SwitchRow({ checked, title, detail, sheet, disabled = false, onToggle }: { checked: boolean; title: string; detail?: string; sheet: boolean; disabled?: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onToggle}
      className={cn("flex w-full items-center justify-between gap-3 rounded-md px-1.5 text-left hover:bg-white/[0.04] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent", sheet ? "min-h-11 py-3" : "py-2")}
    >
      <span className="flex min-w-0 flex-col">
        <span className="text-[13px] font-semibold text-foreground">{title}</span>
        {detail && <span className="text-[11px] text-zinc-500">{detail}</span>}
      </span>
      <span aria-hidden="true" className={cn("relative h-5 w-[34px] flex-shrink-0 rounded-full transition-colors duration-150", checked ? "bg-primary" : "bg-surface-elevated")}>
        <span className={cn("absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition-transform duration-150 motion-reduce:transition-none", checked && "translate-x-[14px]")} />
      </span>
    </button>
  );
}

const PIN = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" /><circle cx="12" cy="10" r="3" /></svg>;
const STATE_CODES = Object.keys(US_STATES) as UsState[];

export function LocationBar({ sheet, className }: { sheet: boolean; className?: string }) {
  const catalog = useBookCatalog();
  const { place, status, retry, setState, setHereOnly } = useBookAvailability();
  if (catalog === null) return null;
  const ready = status === "ready";
  return (
    <div className={cn("flex flex-shrink-0", sheet ? "flex-col" : "items-center gap-2", className)}>
      <div className="flex min-w-0 flex-1 items-center gap-2 px-1.5">
        <span className="flex text-zinc-400">{PIN}</span>
        <span className="flex min-w-0 flex-col">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate text-[13px] font-semibold text-foreground">{place.state === null ? "Set your state" : US_STATES[place.state]}</span>
            <span className={cn("relative flex flex-shrink-0 items-center gap-0.5 rounded px-1 text-[12px] font-medium text-primary hover:bg-primary/10 focus-within:ring-2 focus-within:ring-primary/40", sheet ? "h-11" : "h-6")}>
              Change
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
              <select
                aria-label="Change state"
                disabled={!ready}
                value={place.chosen ? place.state ?? "" : ""}
                onChange={(event) => setState(normalizeUsState(event.target.value))}
                className="absolute inset-0 cursor-pointer opacity-0"
              >
                <option value="">{place.detected === null ? "No state" : `Automatic (${place.detected})`}</option>
                {STATE_CODES.map((code) => <option key={code} value={code}>{US_STATES[code]}</option>)}
              </select>
            </span>
          </span>
          {status === "failed" && (
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate text-[11px] text-zinc-500">Couldn&apos;t load your location settings</span>
              <button type="button" onClick={retry} className={cn("flex flex-shrink-0 items-center rounded px-1 text-[12px] font-medium text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40", sheet ? "h-11" : "h-6")}>Retry</button>
            </span>
          )}
        </span>
      </div>
      <div className={sheet ? undefined : "w-[196px] flex-shrink-0"}>
        <SwitchRow checked={place.hereOnly} disabled={!ready || place.state === null} title="Available here only" sheet={sheet} onToggle={() => setHereOnly(!place.hereOnly)} />
      </div>
    </div>
  );
}

export function checkState(on: number, total: number): CheckState {
  return on === 0 ? "off" : on === total ? "on" : "some";
}

export function ToggleAllRow({ state, label, detail, sheet, onToggle }: { state: CheckState; label: string; detail: string; sheet: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={state === "some" ? "mixed" : state === "on"}
      onClick={onToggle}
      className={cn("flex w-full items-center gap-2 rounded-md px-2 text-left hover:bg-white/[0.06]", sheet ? "h-11" : "h-8")}
    >
      <Check state={state} />
      <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground">{label}</span>
      <span className="data-num flex-shrink-0 text-[11px] text-zinc-500">{detail}</span>
    </button>
  );
}

const groupKey = (group: BookGroup) => group.regulation ?? "other";

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
  );
}

type PickerKind =
  | { readonly kind: "single"; readonly onOnly?: never; readonly onSetMany?: never }
  | { readonly kind: "multi"; readonly onOnly?: (id: string) => void; readonly onSetMany: (ids: readonly string[], on: boolean) => void };

export function BookPanel({
  kind, books, isSelected, onItem, onOnly, onSetMany, sheet, groupKeys, children,
}: PickerKind & {
  books: readonly string[];
  isSelected: (id: string) => boolean;
  onItem: (id: string) => void;
  sheet: boolean;
  groupKeys?: Readonly<Partial<Record<BookRegulation, string>>>;
  children?: ReactNode;
}) {
  const catalog = useBookCatalog();
  const { isHere, place } = useBookAvailability();
  const multi = kind === "multi";
  const groups = useMemo(
    () => pickerGroups(catalog, books).map((group) => ({ ...group, here: group.books.filter(isHere) })),
    [catalog, books, isHere],
  );
  const named = hasNamedGroups(groups);
  const [query, setQuery] = useState("");
  const [shown, setShown] = useState<string | null>(() => {
    const selected = groups.find((group) => group.books.some(isSelected));
    return selected === undefined ? null : groupKey(selected);
  });
  const inputRef = useRef<HTMLInputElement | null>(null);
  const q = query.trim().toLowerCase();
  const current = groups.find((group) => groupKey(group) === shown) ?? groups[0];
  const hits = useMemo<readonly Hit[]>(
    () => (q === "" ? [] : groups.flatMap((group) =>
      group.books
        .filter((id) => sportsbookIdToDisplay(id).toLowerCase().includes(q) || (named && group.label.toLowerCase().includes(q)))
        .map((id) => ({ id, group: named ? group.label : null })))),
    [groups, named, q],
  );

  useEffect(() => { if (!sheet) inputRef.current?.focus(); }, [sheet]);

  const onInputKey = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    const first = hits.find((hit) => isHere(hit.id));
    if (first) onItem(first.id);
  };

  const onCount = (ids: readonly string[]) => ids.filter(isSelected).length;
  const countLabel = (ids: readonly string[]) => {
    const on = onCount(ids);
    return multi && on !== ids.length ? `${on}/${ids.length}` : `${ids.length}`;
  };

  const searchBar = (
    <div className={cn("flex flex-shrink-0 items-center gap-2 border-surface-line", sheet ? "mx-4 mb-2 h-11 rounded-lg border bg-surface-base px-3 focus-within:border-primary/50" : "h-11 border-b px-3 focus-within:border-primary/50")}>
      <span className="flex text-zinc-500"><SearchIcon /></span>
      <input
        ref={inputRef}
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={onInputKey}
        placeholder="Search books"
        aria-label="Search books"
        className={cn("min-w-0 flex-grow bg-transparent text-foreground outline-none placeholder:text-zinc-500 [&::-webkit-search-cancel-button]:hidden", sheet ? "text-[16px]" : "text-[13px]")}
      />
    </div>
  );

  const item = (id: string, hint: string | null) => {
    const on = isSelected(id);
    const name = sportsbookIdToDisplay(id);
    const away = !isHere(id);
    return (
      <div
        key={id}
        className={cn(
          "group/book flex min-w-0 items-center rounded-md",
          away ? NOT_BETTABLE_CLASS : !multi && on ? "bg-primary/10 text-primary" : multi && !on ? "text-zinc-500 hover:bg-white/[0.06] hover:text-zinc-300" : multi ? "text-zinc-200 hover:bg-white/[0.06]" : "text-zinc-300 hover:bg-white/[0.06] hover:text-foreground",
        )}
      >
        <button
          type="button"
          role={multi ? undefined : "menuitemradio"}
          aria-checked={multi ? undefined : on}
          aria-pressed={multi ? on : undefined}
          aria-disabled={away || undefined}
          onClick={away ? undefined : () => onItem(id)}
          className={cn("flex min-w-0 flex-1 items-center gap-2 px-2 text-left text-[13px] font-medium", sheet ? "h-11" : "h-9", away && "cursor-not-allowed")}
        >
          {multi && <Check state={on ? "on" : "off"} />}
          <span className={cn("flex flex-shrink-0", multi && !on && "opacity-40 grayscale")}><SportsbookLogoMark sportsbook={id} size={18} decorative /></span>
          <span className="min-w-0 flex-1 truncate">{name}</span>
          {hint !== null && <span className="flex-shrink-0 text-[10px] font-normal text-zinc-500">{hint}</span>}
        </button>
        {onOnly && isHere(id) && (
          <button
            type="button"
            onClick={() => onOnly(id)}
            aria-label={`Only ${name}`}
            className={cn("mr-1.5 flex-shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/10", sheet ? "flex" : "hidden group-focus-within/book:flex group-hover/book:flex [@media(hover:none)]:flex")}
          >
            Only
          </button>
        )}
      </div>
    );
  };

  const categories = (
    <div className="flex flex-col gap-0.5">
      {groups.map((group) => {
        const key = group.regulation === null ? undefined : groupKeys?.[group.regulation];
        const available = group.here;
        const state = checkState(onCount(available), available.length);
        return (
          <div
            key={groupKey(group)}
            onMouseEnter={() => setShown(groupKey(group))}
            className={cn("flex min-h-11 w-full items-center gap-2 rounded-md px-2", group === current ? "bg-white/[0.07]" : "hover:bg-white/[0.04]")}
          >
            {onSetMany && available.length > 0 && (
              <button
                type="button"
                role="checkbox"
                aria-checked={state === "some" ? "mixed" : state === "on"}
                aria-label={`All ${group.label}`}
                onClick={() => onSetMany(available, state !== "on")}
                className="-m-1 flex flex-shrink-0 rounded p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <Check state={state} />
              </button>
            )}
            <button
              type="button"
              aria-current={group === current ? "true" : undefined}
              onClick={() => setShown(groupKey(group))}
              className="flex min-w-0 flex-1 items-center gap-2 self-stretch rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <span className={cn("min-w-0 flex-1 truncate text-[13px] font-medium", group === current ? "text-foreground" : !multi && group.books.some(isSelected) ? "text-primary" : "text-zinc-300")}>{group.label}</span>
              {key && <kbd aria-hidden="true" title={`On the board, press ${key} to hide or show`} className="flex h-[18px] flex-shrink-0 items-center rounded border border-surface-line px-1.5 font-sans text-[10px] font-medium text-zinc-500 [@media(pointer:coarse)]:hidden">{key}</kbd>}
              {available.length > 0 && <span className="data-num flex-shrink-0 text-[11px] text-zinc-500">{countLabel(available)}</span>}
            </button>
          </div>
        );
      })}
    </div>
  );

  const chips = (
    <ScrollFade className="scrollbar-hide mx-4 mb-2 flex flex-shrink-0 items-center gap-1.5 overflow-x-auto">
      {groups.map((group) => (
        <button key={groupKey(group)} type="button" aria-pressed={group === current} onClick={() => setShown(groupKey(group))} className={cn(chipClass(group === current, true), "whitespace-nowrap")}>
          {group.label}
          {group.here.length > 0 && <span className="data-num text-[11px] font-normal opacity-70">{countLabel(group.here)}</span>}
        </button>
      ))}
    </ScrollFade>
  );

  const groupToggle = sheet && onSetMany && current && q === "" && groups.length > 1 && current.here.length > 0 && (() => {
    const available = current.here;
    const state = checkState(onCount(available), available.length);
    return (
      <div className="pb-1">
        <ToggleAllRow state={state} label={`All ${current.label}`} detail={countLabel(available)} sheet={sheet} onToggle={() => onSetMany(available, state !== "on")} />
      </div>
    );
  })();

  const gridClass = cn("grid gap-0.5", sheet ? "grid-cols-1" : "grid-cols-2");
  const away = (current?.books ?? []).filter((id) => !isHere(id));
  const results = q === ""
    ? (
      <>
        {groupToggle}
        <div className={gridClass}>{(current?.here ?? []).map((id) => item(id, null))}</div>
        {away.length > 0 && (
          <>
            <p className="px-2 pt-3 text-[10px] font-medium uppercase tracking-[0.06em] text-zinc-500">Not available in {place.state}</p>
            <p className="px-2 pb-1 text-[11px] text-zinc-500">Turn off Available here only to use these.</p>
            <div className={gridClass}>{away.map((id) => item(id, null))}</div>
          </>
        )}
      </>
    )
    : hits.length === 0
      ? <div className="px-3 py-8 text-center text-[12px] text-zinc-500">No books match</div>
      : <div className="grid grid-cols-1 gap-0.5">{hits.map((hit) => item(hit.id, isHere(hit.id) ? hit.group : `Not in ${place.state}`))}</div>;

  const split = q === "" && groups.length > 1;

  if (sheet) {
    return (
      <>
        <LocationBar sheet className="mx-4 mb-2" />
        {searchBar}
        {children && <div className="mx-2.5 mb-1 flex-shrink-0">{children}</div>}
        {split && chips}
        <div className="min-h-0 flex-grow overflow-y-auto px-2 pb-[max(1rem,env(safe-area-inset-bottom))]">{results}</div>
      </>
    );
  }

  return (
    <div className="flex min-h-0 flex-col">
      <LocationBar sheet={false} className="border-b border-surface-line px-1.5 py-1" />
      {searchBar}
      {children && <div className="flex-shrink-0 border-b border-surface-line px-1.5 py-1">{children}</div>}
      {split ? (
        <div className={cn("grid h-[440px] min-h-0", multi ? "grid-cols-[236px_minmax(0,1fr)]" : "grid-cols-[196px_minmax(0,1fr)]", "grid-rows-[minmax(0,1fr)]")}>
          <div className="scrollbar-custom min-h-0 overflow-y-auto border-r border-white/[0.06] p-1.5">{categories}</div>
          <div className="scrollbar-custom min-h-0 overflow-y-auto p-1.5">{results}</div>
        </div>
      ) : (
        <div className={cn("scrollbar-custom min-h-0 overflow-y-auto p-1.5", groups.length > 1 ? "h-[440px]" : "max-h-[440px]")}>{results}</div>
      )}
    </div>
  );
}
